import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { Company, Group, Person } from "@mapmycustomers/shared/types/entity";
import { CompanyRef } from "@mapmycustomers/shared/types/entity/Company";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";

import { CreatePersonPayload, FieldValidationErrors } from "@app/store/api/ApiService";
import FileListItem from "@app/types/FileListItem";

export const showGlobalCreatePersonModal = createAction("@component/globalCreatePersonModal/show")<{
  showPostCreatePrompt?: boolean;
} | void>();
export const hideGlobalCreatePersonModal = createAction(
  "@component/globalCreatePersonModal/hide"
)<void>();

export const initialize = createAsyncAction(
  "@component/createPersonModal/show/request",
  "@component/createPersonModal/show/success",
  "@component/createPersonModal/show/failure"
)<
  { fixedCompany?: CompanyRef },
  {
    fileGroupId: string;
    parentCompany?: Company;
  },
  void
>();

export const createPerson = createAsyncAction(
  "@component/createPersonModal/createPerson/request",
  "@component/createPersonModal/createPerson/success",
  "@component/createPersonModal/createPerson/failure"
)<
  {
    callback?: (newPerson: Person) => void;
    checkDuplicates?: boolean;
    customFieldsValues: CustomFieldValue[];
    fieldsValueValidateCallback?: (fieldErrors: FieldValidationErrors[]) => void;
    groupsIdsToAdd: Array<Group["id"]>;
    layoutId: FormLayout["id"];
    person: CreatePersonPayload;
    showNotificationViewButton?: boolean;
  },
  Person,
  { duplicates?: DuplicateEntity[] }
>();

export const resetParentCompany = createAction(
  "@component/createPersonModal/resetParentCompany"
)<void>();

export const uploadPersonFiles = createAsyncAction(
  "@component/createPersonModal/uploadPersonFiles/request",
  "@component/createPersonModal/uploadPersonFiles/success",
  "@component/createPersonModal/uploadPersonFiles/failure"
)<
  {
    callback?: (filesList: FileListItem[]) => void;
    // Overrides the group id this modal minted when it opened. The chat proposal editor borrows
    // this uploader and names its own group, so an upload can be traced to the proposal it came
    // from; PersonRecordPane's own upload takes a caller-supplied id the same way.
    fileGroupId?: string;
    files: (Blob | File)[];
  },
  FileListItem[],
  void
>();

export const removePersonFile = createAsyncAction(
  "@component/createPersonModal/removePersonFile/request",
  "@component/createPersonModal/removePersonFile/success",
  "@component/createPersonModal/removePersonFile/failure"
)<RawFile, RawFile["id"], void>();

export const clearAllUploadedPersonFiles = createAction(
  "@component/createPersonModal/clearAllUploadedPersonFiles"
)<void>();

export const clearDuplicates = createAction("@component/createPersonModal/clearDuplicates")<void>();

export type Actions = ActionType<
  | typeof clearAllUploadedPersonFiles
  | typeof clearDuplicates
  | typeof createPerson
  | typeof hideGlobalCreatePersonModal
  | typeof initialize
  | typeof removePersonFile
  | typeof resetParentCompany
  | typeof showGlobalCreatePersonModal
  | typeof uploadPersonFiles
>;
